import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, Dispatch } from "../../store";

import Typography from "../../components/Typography/Typography";
import Button from "../../components/Button/Button";
import CustomOTPInput from "../../components/CustomOTPInput/CustomOTPInput";
import { useMagic } from "../../contexts/MagicContext";
import { ethers } from "ethers";
import { IUser } from "../../types/interfaces";
import { onBoarding } from "../../http";
import CONSTANTS from "../../contants";
import { ClipLoader } from "react-spinners";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<Dispatch>();

  const { magic } = useMagic();

  const { tokenPromise, email } = useSelector((state: RootState) => state.auth);

  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Cooldown timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [resendCooldown]);

  const handleResencOtp = async () => {
    try {
      setResendLoading(true);
      setError(""); // Clear any previous errors
      setOtp(""); // Clear the OTP input fields
      
      // Cancel the existing token promise if it exists
      if (tokenPromise) {
        try {
          tokenPromise.emit("cancel");
        } catch (error) {
          console.log("Error canceling previous token:", error);
        }
      }

      // Create a new token promise for resending OTP
      const token: any = magic?.auth.loginWithEmailOTP({
        email: email,
        showUI: false,
        lifespan: 3000,
      });

      token
        .on("email-otp-sent", () => {
          console.log("Email OTP sent");
          dispatch.auth.setTokenPromise(token);
          setResendLoading(false);
          setResendCooldown(60); // Start 60 second cooldown
        })
        .on("invalid-email-otp", () => {
          console.log("invalid-email-otp-error ---------");
          setResendLoading(false);
          setError("Invalid OTP");
        })
        // @ts-ignore
        ?.on("error", (reason) => {
          console.log("otp-error ---------", reason);
          setResendLoading(false);
          // setError("Failed to send OTP. Please try again.");
        })
        ?.on("settled", () => {
          console.log("otp-settled ---------");
        });
    } catch (err: any) {
      console.log("Error in handleResencOtp:", err);
      setResendLoading(false);
      // setError("Failed to send OTP. Please try again.");
    }
  };

  const handleVerifyOTP = async () => {
    try {
      if (tokenPromise) {
        tokenPromise.emit("verify-email-otp", otp);
        setLoading(true);
        tokenPromise
          .on("invalid-email-otp", () => {
            console.log("invalid-email-otp-error ---------");
            setError("Invalid OTP");
            setLoading(false);
          })
          // @ts-ignore
          ?.on("done", async (result: string) => {
            console.log("otp-done ---------");
            navigate("/logging-in");
            console.log("isLoggedIn ---------", await magic?.user.isLoggedIn());
            const metadata = await magic?.user.getMetadata();

            if (!tokenPromise || !metadata?.publicAddress) {
              // throw new Error("Magic login failed");
              return null;
            }

            localStorage.setItem("user", JSON.stringify(metadata));
            // setUser(metadata);
            // // }

            localStorage.setItem(
              "access_token",
              await magic?.user.getIdToken()!
            );

            const accessToken = await magic?.user.getIdToken()!;
            const provider = new ethers.BrowserProvider(
              magic?.rpcProvider as any
            );
            const signer = await provider.getSigner();
            const address = await signer.getAddress();

            const user: IUser = {
              walletAddress: address,
              email: metadata.email!,
              firstName: null,
              lastName: null,
            };

            const { data } = await onBoarding(accessToken);
            localStorage.setItem(CONSTANTS.token, data.access_token);
            dispatch.auth.setIsLoggedIn(true);
            dispatch.auth.setUser(user);
            setLoading(false);
            navigate("/documents");
          })
          // @ts-ignore
          ?.on("error", (reason) => {
            console.log("otp-error ---------");
            // setErrors('Error');
          })
          ?.on("settled", () => {
            console.log("otp-settled ---------");
            // is called when the Promise either resolves or rejects
          });
      }
    } catch (err: any) {
    } finally {
    }
  };

  useEffect(() => {
    if (otp.length === 6) {
      handleVerifyOTP();
    }
    setError("");
    // eslint-disable-next-line
  }, [otp]);

  return (
    <div className="flex flex-col items-center py-[90px] pt-[90px] px-[188px] h-[638px]">
      <Typography variant="h1" className="title">
        Enter 6-digit OTP
      </Typography>
      <Typography variant="h5" color="grey" className="mt-[22px]">
        Check your email for the one-time password sent to:
      </Typography>
      <Typography variant="h5" color="blueMedium" className="font-medium">
        {email}
      </Typography>
      <div className="w-[380px] mx-auto my-11">
        <CustomOTPInput
          value={otp}
          onChange={setOtp}
          numInputs={6}
          shouldAutoFocus
          error={!!error}
          disabled={loading}
        />
      </div>
      {(loading || resendLoading) && (
        <div className="flex items-center justify-center my-5">
          <ClipLoader size={30} color="#030A74" />
        </div>
      )}
      {error && (
        <Typography className="text-center my-2 text-red-400">
          {error}
        </Typography>
      )}
      <Typography color="grey">Code will expire in 10 minutes.</Typography>
      <Button
        label={resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : "Resend Code"}
        className="mt-5"
        onClick={handleResencOtp}
        disabled={resendLoading || loading || resendCooldown > 0}
      />
    </div>
  );
};

export default VerifyEmail;
